import { type FC } from 'react';
import { useFragment, graphql } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';
import { getEditionFromNftName } from '../helpers/getEditionFromNftName.ts';
import { MaybeLink } from '../helpers/MaybeLink.tsx';

import styles from './styles/NftItemTileItemDetails.scss';
import { type NftItemTileItemDetails_nftItem$key } from './__generated__/NftItemTileItemDetails_nftItem.graphql.ts';

type Props = {
    nftItem: NftItemTileItemDetails_nftItem$key;
    /** defaults to 'link' */
    creatorDisplay?: 'text' | 'link' | 'none';
    /** defaults to 'link' */
    titleDisplay?: 'text' | 'link' | 'none';
    fireTracking?: () => void;
};

const nftItemFragment = graphql`
    fragment NftItemTileItemDetails_nftItem on NftItem {
        creatorAddress {
            publicProfileUrl
            publicProfile {
                profileName
            }
        }
        metadata {
            ... on NftDigitalArtMetadata {
                name
                edition
            }
        }
        linkData {
            path
            isLinkable
        }
        totalSupply
        isERC1155
    }
`;

export const NftItemTileItemDetails: FC<Props> = ({
    nftItem: nftItemRef,
    creatorDisplay = 'link',
    titleDisplay = 'link',
    fireTracking,
}) => {
    const nftItem = useFragment(nftItemFragment, nftItemRef);
    const { metadata, linkData, creatorAddress } = nftItem;
    const creatorDisplayName = creatorAddress?.publicProfile?.profileName;
    const creatorUrl = creatorDisplay === 'link' ? nftItem.creatorAddress?.publicProfileUrl : null;
    const titleLinkData = titleDisplay === 'link' ? linkData : null;

    return (
        <>
            {creatorDisplay !== 'none' && (
                <MaybeLink href={creatorUrl}>{creatorDisplayName}</MaybeLink>
            )}
            {titleDisplay !== 'none' && (
                <MaybeLink linkData={titleLinkData} className={styles.title} onClick={fireTracking}>
                    {nftItem.metadata?.name}
                </MaybeLink>
            )}
            {nftItem?.isERC1155 ? (
                <FormattedMessage
                    id="nftItemTile.item.edition1155"
                    defaultMessage="Edition of {totalSupply}"
                    values={{ totalSupply: nftItem?.totalSupply }}
                />
            ) : (
                <FormattedMessage
                    id="nftItemTile.item.edition721"
                    defaultMessage="edition: {edition}"
                    values={{
                        edition: getEditionFromNftName(metadata?.name, metadata?.edition) || '1/1',
                    }}
                />
            )}
        </>
    );
};
